import { styled, Theme, CSSObject } from '@mui/material/styles'
import Box from '@mui/material/Box'
import MuiDrawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Avatar from '@mui/material/Avatar'
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import AccountBoxOutlinedIcon from '@mui/icons-material/AccountBoxOutlined'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import Grid from '@mui/material/Grid'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
import { Backdrop } from '@mui/material'

import { useTranslation } from 'react-i18next'
import { useAuth } from '../containers/AuthContainer'
import { useNavigate } from 'react-location'
import { useQueryClient } from 'react-query'

import Menu from '../ui/Menu'

type ComponentProps = {
  handleDrawerOpen: () => void
  handleDrawerClose: () => void
  open: boolean
}

const drawerWidth = 240

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
  backgroundColor: '#252525',
})

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: 0,
  [theme.breakpoints.up('lg')]: {
    width: `calc(${theme.spacing(9)} + 1px)`,
  },
  backgroundColor: '#252525',
})

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  zIndex: theme.zIndex.drawer + 1,
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
  '& .MuiTypography-root': {
    fontSize: 14,
  },
  '& .MuiListItem-root, & .MuiListItemIcon-root ': {
    color: '#a1a1a1',
    padding: '5px 0',
    '&:hover': {
      backgroundColor: '#1d1d1d',
      color: theme.palette.primary.contrastText,
      '& .MuiListItemIcon-root': {
        color: theme.palette.primary.contrastText,
      },
    },
  },
  ' & .MuiListItemIcon-root': {
    minWidth: 71,
    justifyContent: 'center',
  },
}))

function SideNav(props: ComponentProps): JSX.Element {
  const { t } = useTranslation()
  const { logout, getUser } = useAuth()
  const { handleDrawerClose, handleDrawerOpen, open } = props
  const { fileUserProfilePicture, firstName, lastName } = getUser() ?? {}

  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const menuItems = [
    { icon: <AccountBoxOutlinedIcon />, text: t('prospects'), route: `/prospects` },
    { icon: <BusinessOutlinedIcon />, text: t('organizations'), route: `/organizations` },
    { icon: <MonetizationOnOutlinedIcon />, text: t('deals'), route: `/deals` },
    { icon: <FormatListBulletedIcon />, text: t('tasks'), route: `/tasks` },
  ]

  return (
    <Box sx={{ display: 'flex' }}>
      <Backdrop
        sx={{ display: { lg: 'none' }, zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={open}
        onClick={handleDrawerClose}
      />

      <Drawer
        variant="permanent"
        open={open}
        onMouseOver={() => window.innerWidth >= 1200 && handleDrawerOpen()}
        onMouseOut={() => window.innerWidth >= 1200 && handleDrawerClose()}
      >
        <Grid container direction="column" justifyContent="space-between" sx={{ height: '100%' }}>
          <Grid>
            <List component="nav">
              {menuItems.map((item) => (
                <ListItem button key={item.text} onClick={() => navigate({ to: item.route })}>
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.text} />
                </ListItem>
              ))}
            </List>
          </Grid>

          <Grid>
            <List sx={{ padding: 0 }}>
              <ListItem
                sx={{
                  color: 'theme.palette.primary.contrastText',
                  bgcolor: '#1d1d1d',
                }}
                button
                key="user"
              >
                <ListItemIcon>
                  <Avatar alt={firstName} src={`${fileUserProfilePicture}`} />
                </ListItemIcon>
                <ListItemText>
                  <Menu
                    btnLabel={`${firstName} ${lastName}`}
                    icon={<ArrowDropDownIcon />}
                    name="logout"
                    items={[
                      {
                        label: t('logout'),
                        onClick: () => {
                          queryClient.clear()
                          logout()
                        },
                        icon: <PowerSettingsNewIcon sx={{ margin: '0px 8px' }} />,
                      },
                    ]}
                  />
                </ListItemText>
              </ListItem>
            </List>
          </Grid>
        </Grid>
      </Drawer>
    </Box>
  )
}
export default SideNav
